import React, { useState } from 'react';
import { Map, Image as ImageIcon, Plus, FolderOpen, Clock, Eye, Pencil, LayoutGrid, Search, Trash2, Check, X, Loader2 } from 'lucide-react';
import type { PlanosProject, ViewMode } from '../types';
import { getDaysRemaining, formatDaysRemaining, getHostingStatusColor } from '../services/hosting';

/** Active spaces summary (360 Tours + Planos shared) — when present and limit > 0, show dashboard pill */
export interface ActiveSpacesSummary {
  activeCount: number;
  limit: number;
}

interface DashboardProps {
  projects: PlanosProject[];
  onOpenProject: (p: PlanosProject) => void;
  onCreateProject: (viewMode: ViewMode) => void;
  /** Delete a project. Returns true on success so the card can clear its confirm state. */
  onDeleteProject?: (p: PlanosProject) => Promise<boolean> | boolean;
  header: React.ReactNode;
  /** When set, show "X of Y active spaces (360 Tours + Planos)" for unified spatial marketing messaging */
  activeSpaces?: ActiveSpacesSummary | null;
}

const Dashboard: React.FC<DashboardProps> = ({ projects, onOpenProject, onCreateProject, onDeleteProject, header, activeSpaces }) => {
  const PROJECTS_PER_PAGE = 12;
  const [tab, setTab] = useState<'projects' | 'new'>('new');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleConfirmDelete = async (e: React.MouseEvent, p: PlanosProject) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onDeleteProject) return;
    setDeletingId(p.id);
    try {
      const ok = await onDeleteProject(p);
      if (ok) setConfirmDeleteId(null);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredProjects = projects.filter((p) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const matchTitle = (p.title || '').toLowerCase().includes(q);
    const matchJob = (p.internal_job_number || '').toLowerCase().includes(q);
    return matchTitle || matchJob;
  });

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE));
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * PROJECTS_PER_PAGE,
    currentPage * PROJECTS_PER_PAGE
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, tab]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#1a1a1a' }}>
      {header}
      <div className="flex-1 flex flex-col px-4 py-6 sm:py-8 overflow-x-hidden">
        {/* Active Spaces (360 Tours + Planos) — unified dashboard summary */}
        {activeSpaces && activeSpaces.limit > 0 && (
          <div className="max-w-3xl mx-auto w-full mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs border" style={{ backgroundColor: '#252525', borderColor: '#3d3d3d' }}>
              <LayoutGrid size={14} className="text-[#FF3B30]/90" />
              <span className="text-slate-300 font-medium">
                {activeSpaces.activeCount} of {activeSpaces.limit} active spaces
              </span>
              <span className="text-slate-500">(360 Tours + Planos)</span>
            </div>
          </div>
        )}
        {/* Tabs */}
        <div className="flex gap-2 mb-6 sm:mb-8 max-w-3xl mx-auto w-full">
          <button
            onClick={() => setTab('projects')}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-medium transition-all min-h-[48px] touch-manipulation ${
              tab === 'projects' ? 'bg-[#FF3B30] text-white' : 'bg-[#2F2F2F] text-slate-400 hover:text-white border border-[#3d3d3d]'
            }`}
          >
            <FolderOpen size={18} /> Projects
          </button>
          <button
            onClick={() => setTab('new')}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-medium transition-all min-h-[48px] touch-manipulation ${
              tab === 'new' ? 'bg-[#FF3B30] text-white' : 'bg-[#2F2F2F] text-slate-400 hover:text-white border border-[#3d3d3d]'
            }`}
          >
            <Plus size={18} /> New Project
          </button>
        </div>

        {tab === 'projects' ? (
          <div className="max-w-3xl mx-auto w-full">
            <h2 className="text-xl font-bold text-white mb-4">Your projects</h2>
            {projects.length > 0 && (
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <input
                  type="text"
                  placeholder="Search by title or job number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm bg-[#252525] border border-[#3d3d3d] text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#FF3B30]/50 focus:border-[#FF3B30]/50"
                />
              </div>
            )}
            {projects.length === 0 ? (
              <div className="rounded-2xl border border-dashed p-12 text-center" style={{ borderColor: '#3d3d3d' }}>
                <FolderOpen size={48} className="mx-auto mb-4 text-slate-500" />
                <p className="text-slate-400 mb-2">No projects yet</p>
                <p className="text-sm text-slate-500 mb-6">Create your first project with the New Project tab</p>
                <button onClick={() => setTab('new')} className="px-6 py-2 rounded-xl text-white font-medium" style={{ backgroundColor: '#FF3B30' }}>
                  New Project
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  {paginatedProjects.map((p) => {
                  const viewMode = p.view_mode ?? 'map';
                  const isMap = viewMode === 'map';
                  const daysLeft = getDaysRemaining(p.hosting_expires_at ?? null, p.hosting_permanent);
                  const hostColor = getHostingStatusColor(p.hosting_status ?? 'inactive', daysLeft);
                  const isHosted = (p.hosting_status ?? 'inactive') === 'active' || p.hosting_permanent;
                  const isExpired = (p.hosting_status ?? 'inactive') === 'expired' && !p.hosting_permanent;
                  const viewUrl = `/view/${p.slug ?? p.id}`;
                  return (
                    <a
                      key={p.id}
                      href={viewUrl}
                      className="flex flex-col items-start p-4 rounded-2xl border text-left transition-all hover:border-[#FF3B30]/50 hover:bg-[#2F2F2F] active:bg-[#2F2F2F] no-underline min-h-[44px] touch-manipulation" style={{ backgroundColor: '#252525', borderColor: '#3d3d3d' }}
                    >
                      <div className="flex items-center gap-3 w-full mb-2">
                        <span className={`flex items-center justify-center w-10 h-10 rounded-xl shrink-0 ${isMap ? 'bg-blue-500/20 text-blue-400' : 'bg-[#FF3B30]/20 text-[#FF3B30]'}`}>
                          {isMap ? <Map size={22} /> : <ImageIcon size={22} />}
                        </span>
                        <h3 className="font-semibold text-white truncate flex-1 min-w-0">{p.title || 'Untitled Plan'}</h3>
                        {confirmDeleteId === p.id ? (
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-[11px] text-slate-300 mr-0.5 hidden sm:inline">Delete?</span>
                            <button
                              type="button"
                              onClick={(e) => handleConfirmDelete(e, p)}
                              disabled={deletingId === p.id}
                              className="shrink-0 p-2.5 rounded-lg text-white bg-red-600 hover:bg-red-500 transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-60"
                              title="Confirm delete"
                              aria-label="Confirm delete project"
                            >
                              {deletingId === p.id ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDeleteId(null); }}
                              disabled={deletingId === p.id}
                              className="shrink-0 p-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-600/50 transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                              title="Cancel"
                              aria-label="Cancel delete"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenProject(p); }}
                              className="shrink-0 p-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-600/50 transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                              title="Edit"
                              aria-label="Edit project"
                            >
                              <Pencil size={18} />
                            </button>
                            {onDeleteProject && (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDeleteId(p.id); }}
                                className="shrink-0 p-2.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
                                title="Delete"
                                aria-label="Delete project"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${hostColor.bg} ${hostColor.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${hostColor.dot} ${isHosted ? 'animate-pulse' : ''}`} />
                          {isHosted ? 'Live' : isExpired ? 'Expired' : 'Draft'}
                        </span>
                        {isHosted && daysLeft != null && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${daysLeft <= 7 ? 'bg-amber-900/30 text-amber-400' : 'bg-slate-800 text-slate-300'}`}>
                            <Clock size={10} />
                            {formatDaysRemaining(daysLeft)}
                          </span>
                        )}
                        {p.view_count != null && p.view_count > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] text-slate-400 bg-slate-800">
                            <Eye size={10} />
                            {p.view_count} view{p.view_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {isMap ? 'Map overlay' : 'Floor plan only'} · {p.hotspots?.length ?? 0} hotspots
                      </p>
                      {p.internal_job_number && (
                        <p className="text-xs text-slate-400 font-medium mt-1">Job #{p.internal_job_number}</p>
                      )}
                      {p.address && <p className="text-xs text-slate-400 truncate w-full mt-1">{p.address}</p>}
                    </a>
                  );
                })}
                </div>

                {totalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-[#FF3B30] text-white'
                            : 'bg-[#252525] border border-[#3d3d3d] text-slate-300 hover:bg-[#2F2F2F]'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center max-w-3xl mx-auto w-full">
            <h2 className="text-xl font-bold text-white mb-2">How do you want to start?</h2>
            <p className="text-slate-400 text-sm mb-10">Pick one — these are two different ways to build your interactive plan</p>
            <div className="flex flex-col sm:flex-row items-stretch gap-6 sm:gap-8 w-full">
              <button
                onClick={() => onCreateProject('map')}
                className="flex-1 flex flex-col p-6 sm:p-8 rounded-2xl border-2 transition-all hover:scale-[1.02] active:scale-[0.99] hover:shadow-xl text-left group min-h-[44px] touch-manipulation"
                style={{ backgroundColor: '#1e293b', borderColor: '#3b82f6', boxShadow: '0 0 0 1px rgba(59,130,246,0.2)' }}
              >
                <span className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold mb-2">Maps-based</span>
                <Map size={40} className="mb-3 sm:mb-4 text-blue-400 group-hover:scale-110 transition-transform sm:w-12 sm:h-12" />
                <h3 className="text-lg font-bold text-white mb-2">Map overlay</h3>
                <p className="text-sm text-slate-400 leading-relaxed">Place your floor plan on Google Maps. Search address, align with satellite, add hotspots.</p>
              </button>
              <div className="flex flex-col sm:flex-col items-center justify-center shrink-0 text-slate-500 font-medium text-sm py-1">or</div>
              <button
                onClick={() => onCreateProject('floorplan')}
                className="flex-1 flex flex-col p-6 sm:p-8 rounded-2xl border-2 transition-all hover:scale-[1.02] active:scale-[0.99] hover:shadow-xl text-left group min-h-[44px] touch-manipulation"
                style={{ backgroundColor: '#1e293b', borderColor: '#FF3B30', boxShadow: '0 0 0 1px rgba(255,59,48,0.2)' }}
              >
                <span className="text-[10px] uppercase tracking-wider text-red-400 font-semibold mb-2">Standalone</span>
                <ImageIcon size={40} className="mb-3 sm:mb-4 text-red-400 group-hover:scale-110 transition-transform sm:w-12 sm:h-12" />
                <h3 className="text-lg font-bold text-white mb-2">Upload floor plan</h3>
                <p className="text-sm text-slate-400 leading-relaxed">Upload your floor plan image. Add hotspots with photos, 360s, videos. No map.</p>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
